import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

interface RegulatoryUpdate {
  id: string;
  source: string;
  sourceType: string | null;
  sourceUrl: string | null;
  headline: string;
  summary: string | null;
  publishedAt: string | null;
  severityEstimate: string | null;
  substancesAffected: string[];
  productTypesAffected: string[];
}

interface RegulatoryAlert {
  id: string;
  status: string;
  dueDate: string | null;
  matchedProducts: string[];
  matchedSubstances: string[];
  actionRequired: string | null;
  acknowledgedAt: string | null;
  closedAt: string | null;
  update: RegulatoryUpdate | null;
}

export function RegIntelAlertsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canAct = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const canClose = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];

  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');

  const alerts = useQuery({
    queryKey: ['reg-alerts', { status, source }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '200');
      if (status) params.set('status', status);
      try {
        const data = (await api.get(`/api/reg-intel/alerts?${params.toString()}`)).data;
        if (!source) return data;
        // Client-side source filter (API filters status, not source).
        return {
          ...data,
          items: (data.items ?? []).filter(
            (a: RegulatoryAlert) => a.update?.source === source,
          ),
        };
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const acknowledge = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/api/reg-intel/alerts/${id}/acknowledge`, {}),
    onSuccess: () => {
      toast.success('Alert acknowledged');
      qc.invalidateQueries({ queryKey: ['reg-alerts'] });
    },
    onError: (err) => toast.error('Action failed', extractApiError(err).message),
  });

  const close = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/api/reg-intel/alerts/${id}/close`, {
        evidence: 'Closed from Reg-intel feed dashboard.',
      }),
    onSuccess: () => {
      toast.success('Alert closed');
      qc.invalidateQueries({ queryKey: ['reg-alerts'] });
    },
    onError: (err) => toast.error('Action failed', extractApiError(err).message),
  });

  const items: RegulatoryAlert[] = alerts.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Regulatory intelligence</h1>
        <p className="text-sm text-muted-foreground">
          EMA / FDA / MHRA / PMDA alerts with tenant-level acknowledgement and action tracking.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Status</Label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="in_progress">In progress</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <Label>Source</Label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="EMA">EMA</option>
                <option value="PRAC">PRAC</option>
                <option value="MHRA">MHRA</option>
                <option value="FDA">FDA</option>
                <option value="HMA">HMA</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alerts</CardTitle>
          <CardDescription>
            Inline acknowledge and close. Overdue alerts show in red on dashboards.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Headline</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!alerts.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-10 text-center text-sm text-muted-foreground">
                    <Bell className="mx-auto mb-2 h-6 w-6" /> No alerts match.
                  </TableCell>
                </TableRow>
              )}
              {items.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="max-w-md">
                    <div className="truncate text-sm font-medium">
                      {a.update?.headline ?? '-'}
                    </div>
                    {a.update?.summary && (
                      <div className="truncate text-xs text-muted-foreground">
                        {a.update.summary}
                      </div>
                    )}
                    {a.actionRequired && (
                      <div className="mt-1 truncate text-xs text-amber-700 dark:text-amber-400">
                        Action: {a.actionRequired}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{a.update?.source ?? '-'}</TableCell>
                  <TableCell className="text-xs">
                    {a.matchedProducts.length > 0
                      ? a.matchedProducts.join(', ')
                      : a.matchedSubstances.length > 0
                        ? a.matchedSubstances.join(', ')
                        : '-'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {a.update?.publishedAt ? formatDate(a.update.publishedAt) : '-'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {a.dueDate ? formatDate(a.dueDate) : '-'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge s={a.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {a.update?.sourceUrl && (
                        <Button asChild size="sm" variant="ghost">
                          <a href={a.update.sourceUrl} target="_blank" rel="noreferrer">
                            Source
                          </a>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          !canAct ||
                          acknowledge.isPending ||
                          a.status === 'closed' ||
                          a.status === 'acknowledged'
                        }
                        onClick={() => acknowledge.mutate(a.id)}
                      >
                        Acknowledge
                      </Button>
                      <Button
                        size="sm"
                        disabled={!canClose || close.isPending || a.status === 'closed'}
                        onClick={() => close.mutate(a.id)}
                      >
                        Close
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ s }: { s: string | null }) {
  if (!s) return <span className="text-muted-foreground">-</span>;
  const v: Record<string, 'secondary' | 'info' | 'success' | 'warning' | 'destructive'> = {
    open: 'warning',
    acknowledged: 'info',
    in_progress: 'info',
    closed: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}
